
import React, { useState } from 'react';
import { featuredBars } from '../../../mocks/equipmentHireCatalogue';
import { useScrollAnimation, useStaggeredAnimation } from '../../../hooks/useScrollAnimation';

export default function PortableBarsSection() {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const { ref: titleRef, isVisible: titleVisible } = useScrollAnimation();
  const {
    ref: gridRef,
    isVisible: gridVisible,
    getItemDelay,
  } = useStaggeredAnimation(featuredBars.length, { staggerDelay: 150 });

  // Defensive guard – ensure featuredBars is an array
  if (!Array.isArray(featuredBars)) {
    console.error('featuredBars should be an array');
    return null;
  }

  return (
    <section id="portable-bars" className="py-20 bg-white">
      <div className="max-w-6xl mx-auto px-6">
        <div
          ref={titleRef}
          className={`text-center mb-14 transition-all duration-700 ${
            titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-50 border border-amber-200 mb-4">
            <i className="ri-store-2-line text-amber-600 text-sm"></i>
            <span className="text-amber-700 text-xs tracking-widest uppercase font-semibold">
              Premium Collection
            </span>
          </div>
          <h2 className="font-display text-4xl md:text-5xl text-stone-900 mb-4">
            <strong>Portable Bars</strong>
          </h2>
          <p className="text-stone-500 text-sm max-w-xl mx-auto leading-relaxed">
            Handpicked portable bar stations for every style of event. From rustic charm to modern
            luxury — delivered and set up across Melbourne.
          </p>
        </div>

        <div ref={gridRef} className="space-y-6">
          {featuredBars.map((bar, index) => {
            const isExpanded = expandedId === bar.id;

            // Guard against missing mandatory fields
            if (!bar.id || !bar.image || !bar.name) {
              console.warn('Bar item is missing required properties', bar);
              return null;
            }

            return (
              <div
                key={bar.id}
                style={getItemDelay(index)}
                className={`bg-stone-50 rounded-lg overflow-hidden border border-stone-100 hover:border-amber-200 transition-all duration-500 ${
                  gridVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
              >
                <div className="flex flex-col md:flex-row">
                  <div className="md:w-2/5 relative overflow-hidden">
                    <div className="w-full h-64 md:h-80">
                      <img
                        src={bar.image}
                        alt={`${bar.name} - portable bar hire Melbourne`}
                        title={`${bar.name} portable bar hire Melbourne events`}
                        className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-700"
                      />
                    </div>
                    <div className="absolute top-4 left-4 px-3 py-1 bg-amber-500 text-stone-900 text-xs font-bold rounded-full">
                      Popular
                    </div>
                  </div>

                  <div className="md:w-3/5 p-6 md:p-8 flex flex-col justify-between">
                    <div>
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="font-display text-2xl text-stone-900 mb-1">{bar.name}</h3>
                          <p className="text-stone-400 text-xs">
                            {bar.dimensions} &middot; {bar.capacity}
                          </p>
                        </div>
                        <div className="text-right flex-shrink-0 ml-4">
                          <p className="text-2xl font-bold text-amber-600">${bar.price}</p>
                          <p className="text-stone-400 text-xs">{bar.unit}</p>
                        </div>
                      </div>
                      <p className="text-stone-600 text-sm leading-relaxed mb-4">{bar.description}</p>

                      {isExpanded && (
                        <div className="mb-4 animate-fadeIn">
                          <p className="text-xs font-semibold text-stone-700 uppercase tracking-wider mb-2">
                            Included Features
                          </p>
                          <div className="grid grid-cols-2 gap-2">
                            {Array.isArray(bar.features) &&
                              bar.features.map((feature) => (
                                <div key={feature} className="flex items-center gap-2">
                                  <i className="ri-check-line text-amber-500 text-sm"></i>
                                  <span className="text-stone-600 text-xs">{feature}</span>
                                </div>
                              ))}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-3 mt-2">
                      <a
                        href="#hire-inquiry"
                        className="px-6 py-2.5 bg-gradient-to-r from-amber-400 to-amber-500 text-stone-900 text-xs font-semibold rounded-full hover:from-amber-300 hover:to-amber-400 transition-all cursor-pointer whitespace-nowrap shadow-md shadow-amber-500/20"
                      >
                        <i className="ri-mail-send-line mr-1.5"></i>Enquire Now
                      </a>
                      <a
                        href="#booking-calendar"
                        className="px-5 py-2.5 border border-stone-200 text-stone-600 text-xs font-medium rounded-full hover:border-amber-300 hover:text-amber-600 transition-all cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-calendar-line mr-1.5"></i>Check Dates
                      </a>
                      <button
                        onClick={() => setExpandedId(isExpanded ? null : bar.id)}
                        className="ml-auto text-amber-600 text-xs font-medium hover:text-amber-700 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        {isExpanded ? 'Less Info' : 'More Info'}
                        <i
                          className={`ri-arrow-${isExpanded ? 'up' : 'down'}-s-line ml-1`}
                        ></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
